import {
  Alert,
  Badge,
  Box,
  Card,
  Checkbox,
  CloseButton,
  Collapse,
  Divider,
  Group,
  MultiSelect,
  NumberInput,
  Radio,
  SegmentedControl,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
  UnstyledButton,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { IconChevronDown, IconInfoCircle } from "@tabler/icons-react";
import { useState } from "react";
import type { CalculationMethod, FeePolicyDraft, FeePolicyTier } from "../../apis/tenantFeePolicy";
import type { TradingRelationshipOption } from "../../apis/tradingRelationship";
import { TierEditor } from "./TierEditor";

const CURRENCIES = ["MNT", "USD", "EUR", "CNY", "JPY", "KRW"];

// Mantine v9 @mantine/dates uses "YYYY-MM-DD" string values, so minDate is a
// string too. These helpers keep the calendars from offering invalid days.
const toDateStr = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const addDays = (dateStr: string, days: number) => {
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return undefined;
  d.setDate(d.getDate() + days);
  return toDateStr(d);
};

// NumberInput display guard: preserve a stored 0 (a truthy check would blank it
// out and lose the value on reload/auto-save). Only null / "" map to undefined.
const numOrUndef = (x: unknown): number | undefined =>
  x == null || x === "" ? undefined : Number(x);

// Spec §3.3 "Scope → Applicable order types". Empty selection = all order types.
const ORDER_TYPES: { value: string; label: string }[] = [
  { value: "B2B_ORDER",        label: "B2B захиалга" },
  { value: "AGGREGATOR_ORDER", label: "Aggregator захиалга" },
  { value: "INTERNAL_TRADING", label: "Дотоод худалдаа" },
];

// Spec §3.3 fee basis — radios surface all options at once.
const FEE_BASIS_OPTIONS: { value: string; label: string }[] = [
  { value: "ORDER_TOTAL",     label: "Захиалгын нийт дүн" },
  { value: "PAYMENT",         label: "Төлбөрийн дүн" },
  { value: "NET",             label: "Цэвэр дүн" },
  { value: "SUBTOTAL_EX_TAX", label: "Татвар хассан дүн" },
];

// Spec §3.3 deduction mode — labels carry the enum code in parens (mockup parity).
const DEDUCTION_OPTIONS: { value: string; label: string }[] = [
  { value: "AUTO_DEDUCT",      label: "Төлбөрөөс автоматаар суутгах (AUTO_DEDUCT)" },
  { value: "SEPARATE_INVOICE", label: "Тусдаа нэхэмжлэх (SEPARATE_INVOICE)" },
  { value: "OFFSET_PAYOUT",    label: "Тооцооноос суутгах (OFFSET_PAYOUT)" },
  { value: "EXTERNAL",         label: "Системээс гадуур (EXTERNAL)" },
];

// One-line explanation of the selected calculation method (mockup shows an info line).
const CALC_METHOD_HINT: Record<CalculationMethod, string> = {
  PERCENTAGE: "Шимтгэл = суурь дүн × хувь.",
  FLAT:       "Захиалга бүрт ижил тогтмол дүн.",
  TIERED:     "Дүнгийн түвшин тус бүрт өөр хувь (хамгийн ихдээ 3 шатлал).",
};

export type PolicyFormValue = FeePolicyDraft & { rowVersion?: number };

export interface PolicyFormErrors {
  name?: string;
  feeRate?: string;
  minFeeAmount?: string;
  maxFeeAmount?: string;
  effectiveFrom?: string;
  effectiveUntil?: string;
  tierConfig?: string;
}

interface Props {
  value: PolicyFormValue;
  /** Single-field patch callback (mirrors the editor's updateField). */
  onChange: (key: keyof PolicyFormValue, val: unknown) => void;
  /** View mode: every control disabled but values remain readable. */
  readonly?: boolean;
  errors?: PolicyFormErrors;
  /** New drafts may pick scope; an existing policy's scope is fixed. */
  isNew?: boolean;
  effectiveFrom: string | null;
  onEffectiveFromChange?: (val: string | null) => void;
  trList?: TradingRelationshipOption[];
  /** Lock the NAMED name field (amend drafts inherit and keep the source name). */
  lockName?: boolean;
  /** NAMED inline customer picker — flat, deduped roster with business type. */
  customers?: { value: string; name: string; regNumber: string | null; type: string }[];
  /** Currently selected target customers for a NAMED policy. */
  selectedCustomerIds?: string[];
  /** When provided, renders the NAMED customer picker (editor only, not detail view). */
  onSelectedCustomerIdsChange?: (ids: string[]) => void;
}

/**
 * Shared fee-policy field set (spec §3.3). Drives both the editor (readonly=false)
 * and the full-screen detail view (readonly=true) so the layout is identical and
 * every enum is localized in one place.
 */
export function PolicyForm({
  value: form,
  onChange,
  readonly = false,
  errors = {},
  isNew = false,
  effectiveFrom,
  onEffectiveFromChange,
  trList,
  lockName = false,
  customers,
  selectedCustomerIds,
  onSelectedCustomerIdsChange,
}: Props) {
  const charge = form.chargeEndCustomerFee ?? true;
  const [customerSearch, setCustomerSearch] = useState("");

  // NAMED customer picker: grouped add-list (excluding already-picked) + colored
  // type badges. Buyer/supplier get distinct colors so an ordinary user can tell
  // them apart at a glance.
  const customerTypeMeta = (t: string): { label: string; color: string } =>
    t === "SUPPLIER" ? { label: "Нийлүүлэгч", color: "teal" }
      : t === "BUYER" ? { label: "Худалдан авагч", color: "grape" }
      : { label: t || "Бусад", color: "gray" };
  const selectedIds = selectedCustomerIds ?? [];
  const selectedSet = new Set(selectedIds);
  const customerPickerData = (() => {
    const order = ["Худалдан авагч", "Нийлүүлэгч", "Бусад"];
    const groups: Record<string, { value: string; label: string }[]> = {};
    for (const c of customers ?? []) {
      if (selectedSet.has(c.value)) continue;
      const g = customerTypeMeta(c.type).label;
      (groups[g] ??= []).push({ value: c.value, label: c.regNumber ? `${c.name} · ${c.regNumber}` : c.name });
    }
    return order.filter((g) => groups[g]?.length).map((group) => ({ group, items: groups[group] }));
  })();
  const customerById = new Map((customers ?? []).map((c) => [c.value, c]));
  const selectedCustomers = selectedIds
    .map((cid) => customerById.get(cid))
    .filter((c): c is NonNullable<typeof c> => Boolean(c));

  // D9–D12 advanced block (applicable order types / currencies / excluded TRs).
  // Default open in read-only detail view, or when the draft already carries any
  // advanced value, so configured settings are never hidden behind a collapsed panel.
  const hasAdvancedValues =
    (form.applicableOrderTypes?.length ?? 0) > 0 ||
    (form.applicableCurrencies?.length ?? 0) > 0 ||
    (form.excludedTradingRelationshipIds?.length ?? 0) > 0;
  const [advancedOpen, setAdvancedOpen] = useState(readonly || hasAdvancedValues);

  return (
    <Card withBorder padding="md">
      <Stack gap="sm">
        <Divider label="Бодлогын төрөл" labelPosition="left" />
        <Box>
          <Radio.Group
            value={form.scope ?? "DEFAULT"}
            onChange={(v) => onChange("scope", v)}
            readOnly={readonly || (!readonly && !isNew)}>
            <Stack gap="xs">
              <Radio value="DEFAULT" label="Үндсэн (бүх харилцагч)" disabled={!readonly && !isNew} />
              <Radio value="NAMED" label="Нэртэй (тодорхой харилцагч)" disabled={!readonly && !isNew} />
            </Stack>
          </Radio.Group>
          <Text size="xs" c="dimmed" mt={4}>
            Үндсэн — бүх харилцагчид (нэг л идэвхтэй). Нэртэй — тодорхой харилцагчдад зориулна.
          </Text>
        </Box>

        {form.scope === "NAMED" && (
          <TextInput
            label="Бодлогын нэр"
            required
            placeholder="Жишээ: Premium tier, Enterprise flat"
            description={lockName ? "Засварт нэр өөрчлөгдөхгүй." : undefined}
            value={form.name ?? ""}
            onChange={(e) => onChange("name", e.currentTarget.value || null)}
            error={errors.name}
            readOnly={readonly}
            disabled={!readonly && lockName}
          />
        )}

        {form.scope === "NAMED" && onSelectedCustomerIdsChange && (
          <Box>
            <Select
              label="Хамрах харилцагчид"
              description="Энэ бодлого үйлчлэх харилцагчид. Идэвхжүүлэх үед онооно."
              placeholder="Харилцагч хайх / нэмэх"
              data={customerPickerData}
              value={null}
              searchValue={customerSearch}
              onSearchChange={setCustomerSearch}
              onChange={(v) => {
                if (v) {
                  onSelectedCustomerIdsChange([...selectedIds, v]);
                  setCustomerSearch("");
                }
              }}
              searchable
              disabled={readonly}
              nothingFoundMessage="Харилцагч олдсонгүй"
              renderOption={({ option }) => {
                const c = customerById.get(option.value);
                const meta = customerTypeMeta(c?.type ?? "");
                return (
                  <Group justify="space-between" wrap="nowrap" w="100%" gap="sm">
                    <Text size="sm">{c?.name ?? option.label}{c?.regNumber ? ` · ${c.regNumber}` : ""}</Text>
                    <Badge color={meta.color} variant="light" size="sm" tt="none">{meta.label}</Badge>
                  </Group>
                );
              }}
            />
            {selectedCustomers.length > 0 && (
              <Group gap="xs" mt="xs">
                {selectedCustomers.map((c) => {
                  const meta = customerTypeMeta(c.type);
                  return (
                    <Badge
                      key={c.value}
                      color={meta.color}
                      variant="light"
                      size="lg"
                      tt="none"
                      rightSection={
                        readonly ? undefined : (
                          <CloseButton
                            size="xs"
                            variant="transparent"
                            aria-label="Хасах"
                            onClick={() => onSelectedCustomerIdsChange(selectedIds.filter((x) => x !== c.value))}
                          />
                        )
                      }>
                      {c.name}{c.regNumber ? ` · ${c.regNumber}` : ""} · {meta.label}
                    </Badge>
                  );
                })}
              </Group>
            )}
          </Box>
        )}

        <Switch
          label="Эцсийн хэрэглэгчээс шимтгэл авах"
          description="Унтраавал доорх тооцооны тохиргоо хадгалагдах ч идэвхгүй болно."
          checked={charge}
          onChange={(e) => onChange("chargeEndCustomerFee", e.currentTarget.checked)}
          readOnly={readonly}
        />

        <Box style={{ opacity: charge ? 1 : 0.55, transition: "opacity 120ms ease" }}>
          <Stack gap="sm">
            {!charge && (
              <Text size="xs" c="dimmed">
                Эцсийн хэрэглэгчээс шимтгэл авахгүй тул дараах тохиргоо хэрэглэгдэхгүй.
              </Text>
            )}

            <Divider label="Тооцоолох арга" labelPosition="left" />
            <Box>
              <SegmentedControl
                fullWidth
                data={[
                  { value: "PERCENTAGE", label: "Хувиар" },
                  { value: "FLAT",       label: "Тогтмол дүн" },
                  { value: "TIERED",     label: "Шатлалаар" },
                ]}
                value={form.calculationMethod}
                onChange={(v) => onChange("calculationMethod", v as CalculationMethod)}
                readOnly={readonly}
              />
              <Text size="xs" c="dimmed" mt={4}>{CALC_METHOD_HINT[form.calculationMethod]}</Text>
            </Box>

            <Select
              label="Валют"
              description="Тогтмол дүн, доод/дээд хязгаарын валют."
              data={CURRENCIES.map((c) => ({ value: c, label: c }))}
              value={form.feeCurrency}
              onChange={(v) => v && onChange("feeCurrency", v)}
              readOnly={readonly}
            />

            {form.calculationMethod === "PERCENTAGE" && (
              <NumberInput
                label="Шимтгэлийн хувь"
                description="Жишээ: 2.5 = 2.5%. Дээд хязгааргүй — таны бизнесийн шийдвэр."
                suffix="%" min={0} step={0.1} decimalScale={2}
                value={form.feeRate == null || form.feeRate === "" ? undefined : Number((Number(form.feeRate) * 100).toFixed(4))}
                onChange={(v) => onChange("feeRate", v === "" || v == null ? "" : String(Number(v) / 100))}
                error={errors.feeRate}
                readOnly={readonly}
              />
            )}

            {form.calculationMethod === "FLAT" && (
              <NumberInput
                label="Тогтмол дүн" min={0} hideControls thousandSeparator=","
                suffix={` ${form.feeCurrency ?? "MNT"}`}
                value={numOrUndef(form.flatFeeAmount)}
                onChange={(v) => onChange("flatFeeAmount", String(v ?? ""))}
                readOnly={readonly}
              />
            )}

            {form.calculationMethod === "TIERED" && (
              <>
                <TierEditor
                  tiers={(form.tierConfig as FeePolicyTier[]) ?? []}
                  onChange={(t) => onChange("tierConfig", t)}
                  readonly={readonly}
                />
                {errors.tierConfig && <Text size="sm" c="red">{errors.tierConfig}</Text>}
              </>
            )}

            <Divider label="Хязгаар" labelPosition="left" />
            <Group grow>
              <NumberInput
                label="Доод хязгаар (заавал биш)"
                description="Тооцоолсон шимтгэл үүнээс бага бол энэ дүнг авна"
                min={0} hideControls thousandSeparator=","
                suffix={` ${form.feeCurrency ?? "MNT"}`}
                value={numOrUndef(form.minFeeAmount)}
                onChange={(v) => onChange("minFeeAmount", v === "" || v === undefined ? null : String(v))}
                readOnly={readonly}
              />
              <NumberInput
                label="Дээд хязгаар (заавал биш)"
                description="Тооцоолсон шимтгэл үүнээс их бол энэ дүнгээр таслана"
                min={0} hideControls thousandSeparator=","
                suffix={` ${form.feeCurrency ?? "MNT"}`}
                value={numOrUndef(form.maxFeeAmount)}
                onChange={(v) => onChange("maxFeeAmount", v === "" || v === undefined ? null : String(v))}
                error={errors.maxFeeAmount}
                readOnly={readonly}
              />
            </Group>

            <Divider label="Шимтгэл тооцох суурь" labelPosition="left" />
            <Radio.Group
              value={form.feeBasis}
              onChange={(v) => onChange("feeBasis", v)}
              readOnly={readonly}>
              <Stack gap="xs">
                {FEE_BASIS_OPTIONS.map((o) => (
                  <Radio key={o.value} value={o.value} label={o.label} />
                ))}
              </Stack>
            </Radio.Group>

            <Divider label="Хасах арга" labelPosition="left" />
            <Radio.Group
              value={form.deductionMode}
              onChange={(v) => onChange("deductionMode", v)}
              readOnly={readonly}>
              <Stack gap="xs">
                {DEDUCTION_OPTIONS.map((o) => (
                  <Radio key={o.value} value={o.value} label={o.label} />
                ))}
              </Stack>
            </Radio.Group>

            <Divider label="Буцаалтын нөхцөл" labelPosition="left" />
            <Select
              label="Буцаалтын дүрэм"
              data={[
                { value: "PRO_RATA",  label: "Хувь тэнцүүлэн буцаах" },
                { value: "FULL_ONLY", label: "Зөвхөн бүрэн буцаалтад" },
                { value: "NONE",      label: "Шимтгэл буцаахгүй" },
              ]}
              value={form.refundPolicy}
              onChange={(v) => v && onChange("refundPolicy", v)}
              readOnly={readonly}
            />
            <Alert variant="light" color="gray" icon={<IconInfoCircle size={16} />}>
              <Text size="xs">
                Жишээ: ₮100,000 захиалгад ₮2,500 шимтгэл ногдсон бол —
              </Text>
              <Text size="xs">• Хувь тэнцүүлэн: тал нь буцаагдвал ₮1,250 буцаана.</Text>
              <Text size="xs">• Зөвхөн бүрэн буцаалтад: бүрэн буцаалтад л ₮2,500 буцаана.</Text>
              <Text size="xs">• Шимтгэл буцаахгүй: буцаалт хэдийд ч ₮2,500-г үлдээнэ.</Text>
            </Alert>

            <Divider label="Платформын шимтгэлийн харилцаа" labelPosition="left" />
            <Switch
              label="Платформын 1% шимтгэлийг өөрөө үүрэх"
              description="Идэвхтэй: 2.5% шимтгэлээсээ платформын 1%-г та үүрнэ — эцсийн хэрэглэгч зөвхөн 2.5%-г харна. Идэвхгүй: эцсийн хэрэглэгч 2.5% + 1% = 3.5%-г харна."
              checked={form.absorbPlatformFee ?? false}
              onChange={(e) => onChange("absorbPlatformFee", e.currentTarget.checked)}
              readOnly={readonly}
            />

            <Divider
              labelPosition="left"
              label={
                <UnstyledButton
                  onClick={() => setAdvancedOpen((o) => !o)}
                  aria-expanded={advancedOpen}
                  aria-label="Нэмэлт тохиргоо">
                  <Group gap={4} wrap="nowrap">
                    <Text size="sm" fw={500} c="dimmed">Нэмэлт тохиргоо</Text>
                    <IconChevronDown
                      size={14}
                      style={{ transform: advancedOpen ? "rotate(180deg)" : "none", transition: "transform 120ms ease" }}
                    />
                  </Group>
                </UnstyledButton>
              }
            />
            <Collapse expanded={advancedOpen}>
              <Stack gap="sm">
                <Box>
                  <Text size="sm" fw={500} mb={4}>Хамаарах захиалгын төрөл</Text>
                  <Text size="xs" c="dimmed" mb="xs">Хоосон орхивол бүх төрөлд хэрэгжинэ.</Text>
                  <Checkbox.Group
                    value={form.applicableOrderTypes ?? []}
                    onChange={(v) => onChange("applicableOrderTypes", v)}>
                    <Group gap="md">
                      {ORDER_TYPES.map((t) => (
                        <Checkbox key={t.value} value={t.value} label={t.label} readOnly={readonly} />
                      ))}
                    </Group>
                  </Checkbox.Group>
                </Box>

                <MultiSelect
                  label="Хамаарах валют"
                  description="Хоосон орхивол бүх валютад хэрэгжинэ."
                  data={CURRENCIES.map((c) => ({ value: c, label: c }))}
                  value={form.applicableCurrencies ?? []}
                  onChange={(v) => onChange("applicableCurrencies", v)}
                  clearable={!readonly}
                  readOnly={readonly}
                />

                <MultiSelect
                  label="Хасах худалдааны харилцаа (заавал биш)"
                  description={
                    (trList?.length ?? 0) > 0
                      ? "Сонгосон харилцагчдад энэ бодлого хэрэглэгдэхгүй."
                      : "Танд бүртгэлтэй худалдааны харилцаа алга байна."
                  }
                  data={(trList ?? []).map((tr) => ({ value: tr.id, label: tr.label }))}
                  value={form.excludedTradingRelationshipIds ?? []}
                  onChange={(v) => onChange("excludedTradingRelationshipIds", v)}
                  readOnly={readonly}
                  disabled={!readonly && (trList?.length ?? 0) === 0}
                  searchable={!readonly}
                  clearable={!readonly}
                />
              </Stack>
            </Collapse>
          </Stack>
        </Box>

        <Divider label="Хүчинтэй хугацаа" labelPosition="left" />
        <Group grow>
          <DateInput
            label="Эхлэх огноо"
            description="Одоо/өнгөрсөн → шууд идэвхтэй. Ирээдүй → товлосон (SCHEDULED)."
            clearable={!readonly}
            minDate={toDateStr(new Date())}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            value={(effectiveFrom ?? null) as any}
            onChange={(v) => onEffectiveFromChange?.((v as string | null) ?? null)}
            error={errors.effectiveFrom}
            readOnly={readonly}
          />
          <DateInput
            label="Дуусах огноо"
            description="Хоосон орхивол хугацаагүй. Эхлэх огнооноос хойш байх ёстой."
            clearable={!readonly}
            minDate={
              effectiveFrom
                ? (addDays(effectiveFrom, 1) ?? toDateStr(new Date()))
                : toDateStr(new Date())
            }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            value={(form.effectiveUntil ?? null) as any}
            onChange={(v) => onChange("effectiveUntil", (v as string | null) ?? null)}
            error={errors.effectiveUntil}
            readOnly={readonly}
          />
        </Group>
      </Stack>
    </Card>
  );
}
